import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet } from 'api-service';
import { isEmpty } from 'lib';
import {
  adaptBrandDetails,
  adaptBrandHealth,
  adaptProductTrend,
  adaptUserSatisfaction,
  adaptRiskMonitor,
  adaptReviewShare,
  adaptProductPerformance,
  adaptLatestReviews,
  adaptReviewShareByCategory,
  adaptDigikalaComments,
  adaptDigikalaBrandOptions,
  adaptDigikalaCategoryOptions,
  adaptDigikalaProductOptions,
  adaptDigikalaSellerOptions,
  adaptEcommerceBrandCodes,
} from './ecommerce.adapter';
import {
  DIGIKALA_SEARCH_API_PATHS,
  DIGIKALA_SEARCH_ENDPOINT,
  appendSelectedIds,
  appendTimeRange,
  buildDigikalaCommentsParams,
  buildDigikalaLookupParams,
  extractSearchCount,
  extractSearchList,
  resolveBrandCodes,
} from './digikalaSearch.helpers';

export const fetchBrandDetails = createAsyncThunk(
  'ecommerceReducer/fetchBrandDetails',
  async ({ platform }, thunkAPI) => {
    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/entities/user/ecommerce-brand-codes'
      );
      const rawData = response?.data?.result?.data || [];

      if (isEmpty(rawData)) {
        return thunkAPI.rejectWithValue('ecommerce brand codes is empty');
      }

      return adaptBrandDetails(rawData[0], platform);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchBrandHealth = createAsyncThunk(
  'ecommerceReducer/fetchBrandHealth',
  async ({ platform, period }, thunkAPI) => {
    const { brandDetails } = thunkAPI.getState().ecommerceReducer;
    try {
      const response = await apiGet(
        `/voyager/api/v1/ecommerce/${platform}/analytics/brand/health`,
        {
          brand_code: brandDetails.data?.brand?.code,
          period: period || '30d',
        }
      );
      const rawData = response?.data?.result?.data || {};
      return adaptBrandHealth(rawData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchProductTrend = createAsyncThunk(
  'ecommerceReducer/fetchProductTrend',
  async ({ platform, period, interval = 'week' }, thunkAPI) => {
    const { brandDetails } = thunkAPI.getState().ecommerceReducer;
    try {
      const response = await apiGet(
        `/voyager/api/v1/ecommerce/${platform}/analytics/brand/products/count-trend`,
        {
          brand_code: brandDetails.data?.brand?.code,
          period: period || '30d',
          interval,
        }
      );
      const rawData = response?.data?.result?.data || {};
      return adaptProductTrend(rawData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchUserSatisfaction = createAsyncThunk(
  'ecommerceReducer/fetchUserSatisfaction',
  async ({ platform, period, interval = 'week' }, thunkAPI) => {
    const { brandDetails } = thunkAPI.getState().ecommerceReducer;
    try {
      const response = await apiGet(
        `/voyager/api/v1/ecommerce/${platform}/analytics/brand/reviews-satisfaction`,
        {
          brand_code: brandDetails.data?.brand?.code,
          period: period || '30d',
          interval,
        }
      );
      const rawData = response?.data?.result?.data || {};
      return adaptUserSatisfaction(rawData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchRiskMonitor = createAsyncThunk(
  'ecommerceReducer/fetchRiskMonitor',
  async ({ platform, period, mode }, thunkAPI) => {
    const { brandDetails } = thunkAPI.getState().ecommerceReducer;
    try {
      const response = await apiGet(
        `/voyager/api/v1/ecommerce/${platform}/analytics/brand/risk-monitor`,
        {
          brand_code: brandDetails.data?.brand?.code,
          period: period || '30d',
          mode,
        }
      );
      const rawData = response?.data?.result?.data || {};
      return adaptRiskMonitor(rawData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchReviewShare = createAsyncThunk(
  'ecommerceReducer/fetchReviewShare',
  async ({ platform, period }, thunkAPI) => {
    const { brandDetails } = thunkAPI.getState().ecommerceReducer;
    try {
      const response = await apiGet(
        `/voyager/api/v1/ecommerce/${platform}/analytics/brand/review-share`,
        {
          brand_code: brandDetails.data?.brand?.code,
          period: period || '30d',
        }
      );
      const rawData = response?.data?.result?.data || {};
      return adaptReviewShare(rawData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchProductPerformance = createAsyncThunk(
  'ecommerceReducer/fetchProductPerformance',
  async ({ platform, period, mode }, thunkAPI) => {
    const { brandDetails } = thunkAPI.getState().ecommerceReducer;
    try {
      const response = await apiGet(
        `/voyager/api/v1/ecommerce/${platform}/analytics/brand/products/performance`,
        {
          brand_code: brandDetails.data?.brand?.code,
          period: period || '30d',
          mode,
        }
      );
      const rawData = response?.data?.result?.data || {};
      return adaptProductPerformance(rawData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchLatestReviews = createAsyncThunk(
  'ecommerceReducer/fetchLatestReviews',
  async ({ platform }, thunkAPI) => {
    const { brandDetails } = thunkAPI.getState().ecommerceReducer;
    try {
      const response = await apiGet(
        `/voyager/api/v1/ecommerce/${platform}/brand-level/latest-reviews`,
        {
          brand_code: brandDetails.data?.brand?.code,
        }
      );
      const rawData = response?.data?.result?.data || {};
      return adaptLatestReviews(rawData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchReviewShareByCategory = createAsyncThunk(
  'ecommerceReducer/fetchReviewShareByCategory',
  async ({ platform, period, interval = 'week' }, thunkAPI) => {
    const { brandDetails } = thunkAPI.getState().ecommerceReducer;
    try {
      const response = await apiGet(
        `/voyager/api/v1/ecommerce/${platform}/analytics/brand/category-review-share`,
        {
          brand_code: brandDetails.data?.brand?.code,
          period: period || '30d',
          interval,
        }
      );
      const rawData = response?.data?.result?.data || {};
      return adaptReviewShareByCategory(rawData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const getReviewsTableState = (thunkAPI) =>
  thunkAPI.getState().ecommerceReducer.reviewsTable;

export const fetchEcommerceBrandCodes = createAsyncThunk(
  'ecommerceReducer/fetchEcommerceBrandCodes',
  async (_, thunkAPI) => {
    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/entities/user/ecommerce-brand-codes'
      );

      return adaptEcommerceBrandCodes(response?.data?.result?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchDigikalaComments = createAsyncThunk(
  'ecommerceReducer/fetchDigikalaComments',
  async ({ count } = {}, thunkAPI) => {
    try {
      const reviewsTable = getReviewsTableState(thunkAPI);
      const params = buildDigikalaCommentsParams({
        appliedFilters: reviewsTable.appliedFilters,
        seedBrandCode: reviewsTable.seedBrandCode,
        seedBrandCodes: reviewsTable.seedBrandCodes,
        q: reviewsTable.q,
        timeDuration: reviewsTable.timeDuration,
        from: reviewsTable.from,
        to: reviewsTable.to,
        page: reviewsTable.page,
        count,
        sort: reviewsTable.sort,
        sortType: reviewsTable.sortType,
      });

      const response = await apiGet(DIGIKALA_SEARCH_ENDPOINT, {
        api_path: DIGIKALA_SEARCH_API_PATHS.COMMENTS,
        ...params,
      });

      return adaptDigikalaComments(extractSearchList(response));
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchDigikalaCommentsCount = createAsyncThunk(
  'ecommerceReducer/fetchDigikalaCommentsCount',
  async ({ count } = {}, thunkAPI) => {
    try {
      const reviewsTable = getReviewsTableState(thunkAPI);
      const params = buildDigikalaCommentsParams({
        appliedFilters: reviewsTable.appliedFilters,
        seedBrandCode: reviewsTable.seedBrandCode,
        seedBrandCodes: reviewsTable.seedBrandCodes,
        q: reviewsTable.q,
        timeDuration: reviewsTable.timeDuration,
        from: reviewsTable.from,
        to: reviewsTable.to,
        page: reviewsTable.page,
        count,
        sort: reviewsTable.sort,
        sortType: reviewsTable.sortType,
      });

      const response = await apiGet(DIGIKALA_SEARCH_ENDPOINT, {
        api_path: DIGIKALA_SEARCH_API_PATHS.COMMENTS_COUNT,
        ...params,
      });

      return extractSearchCount(response);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchDigikalaBrands = createAsyncThunk(
  'ecommerceReducer/fetchDigikalaBrands',
  async ({ resourceBrandCode, selectedIds, q, page = 1 } = {}, thunkAPI) => {
    try {
      const reviewsTable = getReviewsTableState(thunkAPI);
      const seedBrandCode = resourceBrandCode || reviewsTable.seedBrandCode;

      if (isEmpty(seedBrandCode)) {
        return { items: [], total: 0 };
      }

      const trimmedQuery = String(q ?? '').trim();
      const params = {
        api_path: DIGIKALA_SEARCH_API_PATHS.BRANDS,
        resource_brand_code: seedBrandCode,
        page,
        ...(trimmedQuery.length >= 2 ? { q: trimmedQuery, full_text: 1 } : {}),
      };

      appendTimeRange(params, {
        timeDuration: reviewsTable.timeDuration,
        from: reviewsTable.from,
        to: reviewsTable.to,
      });
      appendSelectedIds(params, selectedIds);

      const response = await apiGet(DIGIKALA_SEARCH_ENDPOINT, params);

      return {
        items: adaptDigikalaBrandOptions(extractSearchList(response)),
        total: extractSearchCount(response),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchDigikalaCategories = createAsyncThunk(
  'ecommerceReducer/fetchDigikalaCategories',
  async (
    { brandCodes, sellerIds, seedBrandCode, selectedIds, q, page = 1 } = {},
    thunkAPI
  ) => {
    try {
      const reviewsTable = getReviewsTableState(thunkAPI);
      const resolvedBrandCodes = resolveBrandCodes(
        { brand: brandCodes },
        seedBrandCode || reviewsTable.seedBrandCode,
        reviewsTable.seedBrandCodes
      );

      const response = await apiGet(DIGIKALA_SEARCH_ENDPOINT, {
        api_path: DIGIKALA_SEARCH_API_PATHS.CATEGORIES,
        ...buildDigikalaLookupParams({
          brandCodes: resolvedBrandCodes,
          seedBrandCodes: reviewsTable.seedBrandCodes,
          sellerIds,
          selectedIds,
          q,
          timeDuration: reviewsTable.timeDuration,
          from: reviewsTable.from,
          to: reviewsTable.to,
          page,
        }),
      });

      return {
        items: adaptDigikalaCategoryOptions(extractSearchList(response)),
        total: extractSearchCount(response),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchDigikalaProducts = createAsyncThunk(
  'ecommerceReducer/fetchDigikalaProducts',
  async (
    { brandCodes, categories, sellerIds, selectedIds, q, page = 1 } = {},
    thunkAPI
  ) => {
    try {
      const reviewsTable = getReviewsTableState(thunkAPI);
      const response = await apiGet(DIGIKALA_SEARCH_ENDPOINT, {
        api_path: DIGIKALA_SEARCH_API_PATHS.PRODUCTS,
        ...buildDigikalaLookupParams({
          seedBrandCode: reviewsTable.seedBrandCode,
          seedBrandCodes: reviewsTable.seedBrandCodes,
          brandCodes,
          categories,
          sellerIds,
          selectedIds,
          q,
          timeDuration: reviewsTable.timeDuration,
          from: reviewsTable.from,
          to: reviewsTable.to,
          page,
        }),
      });

      return {
        items: adaptDigikalaProductOptions(extractSearchList(response)),
        total: extractSearchCount(response),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchDigikalaSellers = createAsyncThunk(
  'ecommerceReducer/fetchDigikalaSellers',
  async (
    { brandCodes, categories, productIds, selectedIds, q, page = 1 } = {},
    thunkAPI
  ) => {
    try {
      const reviewsTable = getReviewsTableState(thunkAPI);
      const response = await apiGet(DIGIKALA_SEARCH_ENDPOINT, {
        api_path: DIGIKALA_SEARCH_API_PATHS.SELLERS,
        ...buildDigikalaLookupParams({
          seedBrandCode: reviewsTable.seedBrandCode,
          seedBrandCodes: reviewsTable.seedBrandCodes,
          brandCodes,
          categories,
          productIds,
          selectedIds,
          q,
          timeDuration: reviewsTable.timeDuration,
          from: reviewsTable.from,
          to: reviewsTable.to,
          page,
        }),
      });

      return {
        items: adaptDigikalaSellerOptions(extractSearchList(response)),
        total: extractSearchCount(response),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
